import { eachDeep } from "deepdash-es/standalone";
import { t } from "i18next";
import { find, has } from "lodash";
import { customDecoding } from "./utilFunctions";

/**
 * Auto-match fields between source and target trees based on properties
 * @param {Array} sourceTreeData - Source tree data (inbound dependency tree)
 * @param {Array} targetTreeData - Target tree data (outbound template tree)
 * @param {number} sourceTab - Source tab index (0: header, 1: body, 2: trailer)
 * @returns {Array} Array of automatic mapping objects
 */
export const findAutomaticMappings = (sourceTreeData, targetTreeData, sourceTab, isRightToRight = false) => {
  // Emit start event
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('autoMappingStarted'));
  }

  if (!sourceTreeData || !targetTreeData || !Array.isArray(sourceTreeData) || !Array.isArray(targetTreeData)) {
    // Emit end event even if no data
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('autoMappingFinished'));
    }
    return [];
  }

  const recordType = ["CUSTOM_DB_FIELD", "PLUGIN", "SEQUENCE", "VARIABLE", "VARIABLE_COLLECTION"];
  const automaticMappings = [];
  const usedTargetKeys = new Set(); // Track which target keys are already mapped
  const mappingKeys = new Set(); // Track unique mapping combinations to avoid duplicates

  // Cache string operations to avoid repeated computations
  const normalizeKey = (key) => {
    if (!key) return "";
    return customDecoding(key).replaceAll("\u001e", "%1E").replaceAll(" ", "+");
  };

  // Extract fields with the proven eachDeep method to ensure we get all nodes
  const sourceFields = isRightToRight ? extractFieldsFromTree(sourceTreeData, true) : extractFieldsFromTree(sourceTreeData);
  
  
  // Create hash maps for O(1) lookup instead of O(n) forEach
  const targetFieldsByKey = new Map();
  const targetFieldsByType = new Map();
  const targetSubNodesByParent = new Map();
  
  // Build optimized lookup structures for target fields
  const targetFields = extractFieldsFromTree(targetTreeData, true);
  
  let totalSubNodes = 0;
  let totalDirectFields = 0;
  
  for (const targetField of targetFields) {
    const targetNodeData = targetField.node;
    const targetPARENTKEY = normalizeKey(targetNodeData.PARENT || targetNodeData.SOURCE_PARENT || "");
    const targetMAPNAME = targetNodeData.MAPNAME || targetNodeData.LOOP_SOURCE_NAME || "";
    const targetRECORD_TYPE = targetNodeData.RECORD_TYPE || targetNodeData.SOURCE_TYPE || "";
    
    // Create lookup key for fast matching - this handles ALL extracted fields, regardless of nesting
    const lookupKey = `${targetPARENTKEY}|${targetMAPNAME}|${targetRECORD_TYPE}`;
    
    if (!targetFieldsByKey.has(lookupKey)) {
      targetFieldsByKey.set(lookupKey, []);
    }
    targetFieldsByKey.get(lookupKey).push(targetField);
    totalDirectFields++;
    
    // Index by icon display for special node types
    const iconDisplay = targetNodeData.ICON_DISPLAY;
    if (iconDisplay) {
      if (!targetFieldsByType.has(iconDisplay)) {
        targetFieldsByType.set(iconDisplay, []);
      }
      targetFieldsByType.get(iconDisplay).push(targetField);
    }

    // Pre-process sub-nodes for complex target types (this is for additional sub-node handling)
    if (["QUERY", "PLUGIN", "VARIABLE", "SEQUENCE", "VARIABLE_COLLECTION", "TRACKING_REPORT_ITEM", "CONDITION_IF"].includes(iconDisplay)) {
      const subNodesArray = targetNodeData?.VARIABLE || targetNodeData?.IN_ARG || targetNodeData?.TRACKING_REPORT_DETAILS || targetNodeData?.CONDITIONLIST?.CONDITION || [];
      totalSubNodes += subNodesArray.length;
      
      
      for (const subNode of subNodesArray) {
        const subTargetPARENTKEY = normalizeKey(subNode.PARENT || subNode.SOURCE_PARENT || subNode?.TRACKING_REPORT_FIELD?.PARENT || "");
        const subTargetMAPNAME = subNode.MAPNAME || subNode.LOOP_SOURCE_NAME || subNode?.TRACKING_REPORT_FIELD?.MAPNAME || "";
        const subTargetRECORD_TYPE = subNode.RECORD_TYPE || subNode?.TRACKING_REPORT_FIELD?.RECORD_TYPE || "";
        
        const subLookupKey = `${subTargetPARENTKEY}|${subTargetMAPNAME}|${subTargetRECORD_TYPE}`;
        
        if (!targetSubNodesByParent.has(subLookupKey)) {
          targetSubNodesByParent.set(subLookupKey, []);
        }
        targetSubNodesByParent.get(subLookupKey).push({ targetField, subNode });

        // Handle conditional if mapping2
        if (iconDisplay === "CONDITION_IF") {
          const subTargetPARENTKEY2 = normalizeKey(subNode.PARENT2 || subNode.SOURCE_PARENT || subNode?.TRACKING_REPORT_FIELD?.PARENT || "");
          const subTargetMAPNAME2 = subNode.MAPNAME2 || subNode.LOOP_SOURCE_NAME || subNode?.TRACKING_REPORT_FIELD?.MAPNAME || "";
          const subTargetRECORD_TYPE2 = subNode.RECORD_TYPE2 || subNode?.TRACKING_REPORT_FIELD?.RECORD_TYPE || "";
          
          const subLookupKey2 = `${subTargetPARENTKEY2}|${subTargetMAPNAME2}|${subTargetRECORD_TYPE2}`;
          
          if (!targetSubNodesByParent.has(subLookupKey2)) {
            targetSubNodesByParent.set(subLookupKey2, []);
          }
          targetSubNodesByParent.get(subLookupKey2).push({ targetField, subNode });
        }
      }
    }
  }


  // Optimized matching function using hash lookups
  const findMatchesForNode = (node, PARENTKEY, MAPNAME, RECORD_TYPE, sourceField) => {
    const normalizedParentKey = normalizeKey(PARENTKEY);
    const lookupKey = `${normalizedParentKey}|${MAPNAME}|${RECORD_TYPE}`;
    const mappingType = isRightToRight ? 'right-to-right' : 'left-to-right';
    
    // Create unique mapping identifier to avoid duplicates
    const createMappingId = (sourceKey, targetKey) => `${sourceKey}->${targetKey}`;
    
    if (isRightToRight) {
      
      // Handle right-to-right mappings with optimized lookups
      if (recordType.includes(RECORD_TYPE)) {
        const targetType = RECORD_TYPE === "CUSTOM_DB_FIELD" ? "QUERY" : RECORD_TYPE;
        const candidateTargets = targetFieldsByType.get(targetType) || [];
        for (const targetField of candidateTargets) {
          const targetNodeData = targetField.node;
          let hasMatch = false;
          
          // Primary mapping logic
          if (RECORD_TYPE === "VARIABLE" && targetNodeData.ICON_DISPLAY === "VARIABLE") {
            hasMatch = node.MAPNAME === targetNodeData.NAME;
          } else if (RECORD_TYPE === "SEQUENCE" && targetNodeData.ICON_DISPLAY === "SEQUENCE") {
            hasMatch = node.MAPNAME === targetNodeData.NAME;
          } else if (RECORD_TYPE === "PLUGIN" && targetNodeData.ICON_DISPLAY === "PLUGIN") {
            hasMatch = node.PLUGIN_NAME === targetNodeData.NAME;
          } else if (RECORD_TYPE === "CUSTOM_DB_FIELD" && targetNodeData.ICON_DISPLAY === "QUERY") {
            hasMatch = node.CUSTOM_DB_FIELD === targetNodeData.NAME || MAPNAME === targetNodeData.NAME;
          } else if (RECORD_TYPE === "VARIABLE_COLLECTION" && targetNodeData.ICON_DISPLAY === "VARIABLE_COLLECTION") {
            hasMatch = node.MAPNAME === targetNodeData.NAME || MAPNAME === targetNodeData.NAME;
          }

          // Secondary mapping (Mapping2) for conditional if
          if (!hasMatch && node.RECORD_TYPE2) {
            if (node.RECORD_TYPE2 === "VARIABLE" && targetNodeData.ICON_DISPLAY === "VARIABLE") {
              hasMatch = node.MAPNAME2 === targetNodeData.NAME;
            } else if (node.RECORD_TYPE2 === "SEQUENCE" && targetNodeData.ICON_DISPLAY === "SEQUENCE") {
              hasMatch = node.MAPNAME2 === targetNodeData.NAME;
            } else if (node.RECORD_TYPE2 === "PLUGIN" && targetNodeData.ICON_DISPLAY === "PLUGIN") {
              hasMatch = node.PLUGIN_NAME2 === targetNodeData.NAME;
            } else if (node.RECORD_TYPE2 === "CUSTOM_DB_FIELD" && targetNodeData.ICON_DISPLAY === "QUERY") {
              hasMatch = node.CUSTOM_DB_FIELD2 === targetNodeData.NAME || MAPNAME === targetNodeData.NAME;
            } else if (node.RECORD_TYPE2 === "VARIABLE_COLLECTION" && targetNodeData.ICON_DISPLAY === "VARIABLE_COLLECTION") {
              hasMatch = node.MAPNAME2 === targetNodeData.NAME || MAPNAME === targetNodeData.NAME;
            }
          }

          if (hasMatch) {
            addMappingIfValid(node, targetNodeData, sourceField, targetField, mappingType);
          }
        }
      } else {
        // Handle non-record types for right-to-right
        if ((RECORD_TYPE === "LOOP_COUNT" || RECORD_TYPE === "GLOBAL_SEGMENT_COUNT")) {
          for (const targetField of targetFields) {
            if (targetField.node.DESCRIPTION === MAPNAME) {
              addMappingIfValid(node, targetField.node, sourceField, targetField, mappingType);
            }
          }
        }
      }
    } else {
      // Handle left-to-right mappings - use more comprehensive approach
      let foundMatch = false;
      
      // Check sub-nodes first (for complex target types)
      const subNodeMatches = targetSubNodesByParent.get(lookupKey) || [];
      if (subNodeMatches.length > 0) {
        for (const { targetField, subNode } of subNodeMatches) {
          addMappingIfValid(node, targetField.node, sourceField, targetField, mappingType);
          foundMatch = true;
        }
      }

      // Check for special internal count matches
      const internalCountKeys = [
        `${normalizedParentKey}|INTERNAL_TOTAL_RECTYPE_COUNT|${RECORD_TYPE}`,
        `${normalizedParentKey}|INTERNAL_CURRENT_RECTYPE_COUNT|${RECORD_TYPE}`
      ];
      
      for (const countKey of internalCountKeys) {
        const countMatches = targetSubNodesByParent.get(countKey) || [];
        for (const { targetField } of countMatches) {
          if (!node.hasOwnProperty("MAPNAME")) {
            addMappingIfValid(node, targetField.node, sourceField, targetField, mappingType);
            foundMatch = true;
          }
        }
      }

      // Direct field matches (non-complex types)
      const directMatches = targetFieldsByKey.get(lookupKey) || [];
      for (const targetField of directMatches) {
        const targetNodeData = targetField.node;
        if (!["PLUGIN", "VARIABLE", "SEQUENCE", "VARIABLE_COLLECTION", "TRACKING_REPORT_ITEM", "CONDITION_IF"].includes(targetNodeData.ICON_DISPLAY)) {
          addMappingIfValid(node, targetNodeData, sourceField, targetField, mappingType);
          foundMatch = true;
        }
      }

      // Fallback: Check all target fields for more comprehensive matching if no hash matches found
      if (!foundMatch) {
        let fallbackAttempts = 0;
        let potentialMatches = 0;
        
        for (const targetField of targetFields) {
          fallbackAttempts++;
          const targetNodeData = targetField.node;
          const targetPARENTKEY = normalizeKey(targetNodeData.PARENT || targetNodeData.SOURCE_PARENT || "");
          const targetMAPNAME = targetNodeData.MAPNAME || targetNodeData.LOOP_SOURCE_NAME || "";
          const targetRECORD_TYPE = targetNodeData.RECORD_TYPE || targetNodeData.SOURCE_TYPE || "";
          
          // Check for various match types
          let hasMatch = false;
          
          // Standard exact match
          if (normalizedParentKey === targetPARENTKEY && MAPNAME === targetMAPNAME && RECORD_TYPE === targetRECORD_TYPE) {
            hasMatch = true;
            potentialMatches++;
          }
          // Internal count match
          else if (normalizedParentKey === targetPARENTKEY && 
                   (targetMAPNAME === "INTERNAL_TOTAL_RECTYPE_COUNT" || targetMAPNAME === "INTERNAL_CURRENT_RECTYPE_COUNT") && 
                   !node.hasOwnProperty("MAPNAME") && RECORD_TYPE === targetRECORD_TYPE) {
            hasMatch = true;
            potentialMatches++;
          }
          // Inbound data match
          else if (normalizedParentKey === targetPARENTKEY && 
                   node.RECORD_TYPE === targetNodeData.LOOP_SOURCE_NAME && 
                   targetNodeData.SOURCE_TYPE === "INBOUND_DATA" && 
                   !node.hasOwnProperty("MAPNAME")) {
            hasMatch = true;
            potentialMatches++;
          }
          
          if (hasMatch) {
            addMappingIfValid(node, targetNodeData, sourceField, targetField, mappingType);
            foundMatch = true;
          }
          
        }
      }
    }

    function addMappingIfValid(sourceNode, targetNode, sourceField, targetField, mappingType) {
      const mappingId = createMappingId(sourceField.key, targetField.key);
      validationAttempts++;
      
      // Skip if this exact mapping already exists
      if (mappingKeys.has(mappingId)) {
        return;
      }


      // Validate the mapping - be more lenient for automatic mappings
      const validation = validateMapping(sourceNode, targetNode, !isRightToRight, mappingType, true);
      const validRecordTypes = ["CUSTOM_DB_FIELD", "PLUGIN", "SEQUENCE", "VARIABLE", "VARIABLE_COLLECTION", "TRACKING_REPORT_ITEM", "CONDITION_IF"];
      
      // Be more lenient for automatic mappings - accept more combinations
      const shouldAccept = validation.isValid || 
                          isRightToRight || 
                          validRecordTypes.includes(targetNode.ICON_DISPLAY) ||
                          (sourceNode.MAPNAME && targetNode.MAPNAME && sourceNode.MAPNAME === targetNode.MAPNAME) ||
                          (sourceNode.FIELD_NAME && targetNode.FIELD_NAME && sourceNode.FIELD_NAME === targetNode.FIELD_NAME) ||
                          (sourceNode.ELEMENT_NAME && targetNode.ELEMENT_NAME && sourceNode.ELEMENT_NAME === targetNode.ELEMENT_NAME);

      if (shouldAccept) {
        automaticMappings.push({
          sourceKey: sourceField.key,
          targetKey: targetField.key,
          sourceTab: sourceTab,
          mappingType: mappingType,
          isAutomatic: true,
          sourceNodeId: sourceNode.nodeId,
          targetNodeId: targetNode.nodeId
        });
        
        mappingKeys.add(mappingId);
        usedTargetKeys.add(targetField.key);
        
      } else {
        validationFailures++;

      }
    }
  };

  // Process source fields efficiently
  let processedSourceFields = 0;
  let attemptedMatches = 0;
  let validationAttempts = 0;
  let validationFailures = 0;
  for (const sourceField of sourceFields) {
    processedSourceFields++;
    const sourceNode = sourceField.node;
    const iconDisplay = sourceNode.ICON_DISPLAY;
    
    if (["QUERY", "PLUGIN", "SEQUENCE", "VARIABLE", "VARIABLE_COLLECTION", "TRACKING_REPORT_ITEM", "CONDITION_IF"].includes(iconDisplay)) {
      const subNodesArray = sourceNode.VARIABLE || sourceNode.IN_ARG || sourceNode.TRACKING_REPORT_DETAILS || sourceNode.CONDITIONLIST?.CONDITION || [];
      
      for (const subNode of subNodesArray) {
        const PARENTKEY = subNode.PARENT || subNode.SOURCE_PARENT || subNode.TRACKING_REPORT_FIELD?.PARENT || subNode.PARENT2 || "";
        const MAPNAME = subNode.MAPNAME || subNode.LOOP_SOURCE_NAME || subNode.TRACKING_REPORT_FIELD?.MAPNAME || subNode.MAPNAME2 || "";
        const RECORD_TYPE = subNode.RECORD_TYPE || subNode.TRACKING_REPORT_FIELD?.RECORD_TYPE || subNode.RECORD_TYPE2 || "";
        

        if (PARENTKEY || MAPNAME || RECORD_TYPE) {
          attemptedMatches++;
          findMatchesForNode(subNode.TRACKING_REPORT_FIELD || subNode, PARENTKEY, MAPNAME, RECORD_TYPE, sourceField);
        }
        
        // Handle conditional if mapping2
        if (iconDisplay === "CONDITION_IF") {
          const PARENTKEY2 = subNode.PARENT2 || subNode.SOURCE_PARENT || subNode.TRACKING_REPORT_FIELD?.PARENT || "";
          const MAPNAME2 = subNode.MAPNAME2 || subNode.LOOP_SOURCE_NAME || subNode.TRACKING_REPORT_FIELD?.MAPNAME || "";
          const RECORD_TYPE2 = subNode.RECORD_TYPE2 || subNode.TRACKING_REPORT_FIELD?.RECORD_TYPE || "";
          
          if (PARENTKEY2 || MAPNAME2 || RECORD_TYPE2) {
            attemptedMatches++;
            findMatchesForNode(subNode, PARENTKEY2, MAPNAME2, RECORD_TYPE2, sourceField);
          }
        }
      }
    } else {
      
      const PARENTKEY = normalizeKey(sourceNode.PARENT || sourceNode.SOURCE_PARENT || "");
      const MAPNAME = sourceNode.MAPNAME || sourceNode.LOOP_SOURCE_NAME || "";
      const RECORD_TYPE = sourceNode.RECORD_TYPE || sourceNode.SOURCE_TYPE || "";
      
      if (PARENTKEY || MAPNAME || RECORD_TYPE) {
        attemptedMatches++;
        findMatchesForNode(sourceNode, PARENTKEY, MAPNAME, RECORD_TYPE, sourceField);
      }
    }
  }
  

  
  // Emit end event
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('autoMappingFinished'));
  }
  
  return automaticMappings;
};

/**
 * Auto-match fields across all tabs (cross-tab automatic mapping) - Optimized
 * Enforces rule: One source can map to multiple targets, but each target can only have one source
 * @param {Object} inboundDependencyTree - Complete inbound dependency tree with all tabs
 * @param {Object} outboundRightTree - Complete outbound right tree with all tabs
 * @returns {Array} Array of automatic mapping objects including cross-tab mappings
 */
export const findCrossTabAutomaticMappings = (inboundDependencyTree, outboundRightTree) => {
  // Emit start event for cross-tab mapping
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('autoMappingStarted'));
  }

  if (!inboundDependencyTree || !outboundRightTree) {
    // Emit end event even if no data
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('autoMappingFinished'));
    }
    return [];
  }

  const tabMapping = ['header', 'body', 'trailer'];
  const allPotentialMappings = []; // Store all potential mappings with scores
  const recordType = ["CUSTOM_DB_FIELD", "PLUGIN", "SEQUENCE", "VARIABLE", "VARIABLE_COLLECTION"];
  
  // Cache for extracted fields to avoid re-extraction
  const sourceFieldsCache = new Map();
  const targetFieldsCache = new Map();

  // Pre-extract all fields for better performance
  tabMapping.forEach((tabName, tabIndex) => {
    const sourceTreeData = Array.isArray(inboundDependencyTree[tabName]) 
      ? inboundDependencyTree[tabName] 
      : [];
    const targetTreeData = outboundRightTree[tabName]?.translatedData || [];
    
    if (sourceTreeData.length > 0) {
      sourceFieldsCache.set(tabIndex, extractFieldsFromTree(sourceTreeData));
    }
    
    if (targetTreeData.length > 0) {
      targetFieldsCache.set(tabIndex, extractFieldsFromTree(targetTreeData, true));
    }
  });

  // Create lookup maps for target fields for O(1) access
  const targetFieldsByKey = new Map();
  const targetFieldsByType = new Map();
  const targetSubNodesByParent = new Map();
  
  tabMapping.forEach((targetTabName, targetTabIndex) => {
    const targetFields = targetFieldsCache.get(targetTabIndex) || [];
    
    for (const targetField of targetFields) {
      const targetNodeData = targetField.node;
      const targetPARENTKEY = targetNodeData.PARENT || targetNodeData.SOURCE_PARENT || "";
      const targetMAPNAME = targetNodeData.MAPNAME || targetNodeData.LOOP_SOURCE_NAME || "";
      const targetRECORD_TYPE = targetNodeData.RECORD_TYPE || targetNodeData.SOURCE_TYPE || "";
      
      const lookupKey = `${targetPARENTKEY}|${targetMAPNAME}|${targetRECORD_TYPE}`;
      
      if (!targetFieldsByKey.has(lookupKey)) {
        targetFieldsByKey.set(lookupKey, []);
      }
      
      targetFieldsByKey.get(lookupKey).push({
        ...targetField,
        targetTab: targetTabIndex
      });
      
      // Index by icon display for special node types
      const iconDisplay = targetNodeData.ICON_DISPLAY;
      if (iconDisplay) {
        if (!targetFieldsByType.has(iconDisplay)) {
          targetFieldsByType.set(iconDisplay, []);
        }
        targetFieldsByType.get(iconDisplay).push({
          ...targetField,
          targetTab: targetTabIndex
        });
      }

      // Pre-process sub-nodes for complex target types
      if (["QUERY", "PLUGIN", "VARIABLE", "SEQUENCE", "VARIABLE_COLLECTION", "TRACKING_REPORT_ITEM", "CONDITION_IF"].includes(iconDisplay)) {
        const subNodesArray = targetNodeData?.VARIABLE || targetNodeData?.IN_ARG || targetNodeData?.TRACKING_REPORT_DETAILS || targetNodeData?.CONDITIONLIST?.CONDITION || [];
        
        for (const subNode of subNodesArray) {
          const subTargetPARENTKEY = subNode.PARENT || subNode.SOURCE_PARENT || subNode?.TRACKING_REPORT_FIELD?.PARENT || "";
          const subTargetMAPNAME = subNode.MAPNAME || subNode.LOOP_SOURCE_NAME || subNode?.TRACKING_REPORT_FIELD?.MAPNAME || "";
          const subTargetRECORD_TYPE = subNode.RECORD_TYPE || subNode?.TRACKING_REPORT_FIELD?.RECORD_TYPE || "";
          
          const subLookupKey = `${subTargetPARENTKEY}|${subTargetMAPNAME}|${subTargetRECORD_TYPE}`;
          
          if (!targetSubNodesByParent.has(subLookupKey)) {
            targetSubNodesByParent.set(subLookupKey, []);
          }
          targetSubNodesByParent.get(subLookupKey).push({ 
            targetField: { ...targetField, targetTab: targetTabIndex }, 
            subNode 
          });

          // Handle conditional if mapping2
          if (iconDisplay === "CONDITION_IF") {
            const subTargetPARENTKEY2 = subNode.PARENT2 || subNode.SOURCE_PARENT || subNode?.TRACKING_REPORT_FIELD?.PARENT || "";
            const subTargetMAPNAME2 = subNode.MAPNAME2 || subNode.LOOP_SOURCE_NAME || subNode?.TRACKING_REPORT_FIELD?.MAPNAME || "";
            const subTargetRECORD_TYPE2 = subNode.RECORD_TYPE2 || subNode?.TRACKING_REPORT_FIELD?.RECORD_TYPE || "";
            
            const subLookupKey2 = `${subTargetPARENTKEY2}|${subTargetMAPNAME2}|${subTargetRECORD_TYPE2}`;
            
            if (!targetSubNodesByParent.has(subLookupKey2)) {
              targetSubNodesByParent.set(subLookupKey2, []);
            }
            targetSubNodesByParent.get(subLookupKey2).push({ 
              targetField: { ...targetField, targetTab: targetTabIndex }, 
              subNode 
            });
          }
        }
      }
    }
  });

  
  // Step 1: Find all potential mappings and score them efficiently
  tabMapping.forEach((sourceTabName, sourceTabIndex) => {
    const sourceFields = sourceFieldsCache.get(sourceTabIndex) || [];
    
    if (sourceFields.length === 0) return;

    // Process each source field
    for (const sourceField of sourceFields) {
      const sourceNode = sourceField.node;
      const iconDisplay = sourceNode.ICON_DISPLAY;
      
      if (["QUERY", "PLUGIN", "SEQUENCE", "VARIABLE", "VARIABLE_COLLECTION", "TRACKING_REPORT_ITEM", "CONDITION_IF"].includes(iconDisplay)) {
        // Handle special nodes with sub-nodes
        const subNodesArray = sourceNode.VARIABLE || sourceNode.IN_ARG || sourceNode.TRACKING_REPORT_DETAILS || sourceNode.CONDITIONLIST?.CONDITION || [];
        
        for (const subNode of subNodesArray) {
          const PARENTKEY = subNode.PARENT || subNode.SOURCE_PARENT || subNode.TRACKING_REPORT_FIELD?.PARENT || subNode.PARENT2 || "";
          const MAPNAME = subNode.MAPNAME || subNode.LOOP_SOURCE_NAME || subNode.TRACKING_REPORT_FIELD?.MAPNAME || subNode.MAPNAME2 || "";
          const RECORD_TYPE = subNode.RECORD_TYPE || subNode.TRACKING_REPORT_FIELD?.RECORD_TYPE || subNode.RECORD_TYPE2 || "";
          
          if (PARENTKEY || MAPNAME || RECORD_TYPE) {
            findMatchesForNode(subNode.TRACKING_REPORT_FIELD || subNode, PARENTKEY, MAPNAME, RECORD_TYPE, sourceField, sourceTabIndex);
          }
          
          // Handle conditional if mapping2
          if (iconDisplay === "CONDITION_IF") {
            const PARENTKEY2 = subNode.PARENT2 || subNode.SOURCE_PARENT || subNode.TRACKING_REPORT_FIELD?.PARENT || "";
            const MAPNAME2 = subNode.MAPNAME2 || subNode.LOOP_SOURCE_NAME || subNode.TRACKING_REPORT_FIELD?.MAPNAME || "";
            const RECORD_TYPE2 = subNode.RECORD_TYPE2 || subNode.TRACKING_REPORT_FIELD?.RECORD_TYPE || "";
            
            if (PARENTKEY2 || MAPNAME2 || RECORD_TYPE2) {
              findMatchesForNode(subNode, PARENTKEY2, MAPNAME2, RECORD_TYPE2, sourceField, sourceTabIndex);
            }
          }
        }
      } else {
        // Handle regular field nodes
        const PARENTKEY = sourceNode.PARENT || sourceNode.SOURCE_PARENT || "";
        const MAPNAME = sourceNode.MAPNAME || sourceNode.LOOP_SOURCE_NAME || "";
        const RECORD_TYPE = sourceNode.RECORD_TYPE || sourceNode.SOURCE_TYPE || "";

        if (PARENTKEY || MAPNAME || RECORD_TYPE) {
          findMatchesForNode(sourceNode, PARENTKEY, MAPNAME, RECORD_TYPE, sourceField, sourceTabIndex);
        }
      }
    }
  });

  function findMatchesForNode(node, PARENTKEY, MAPNAME, RECORD_TYPE, sourceField, sourceTabIndex) {
    // Handle right-to-right mappings for special record types
    if (recordType.includes(RECORD_TYPE)) {
      const targetType = RECORD_TYPE === "CUSTOM_DB_FIELD" ? "QUERY" : RECORD_TYPE;
      const candidateTargets = targetFieldsByType.get(targetType) || [];
      
      for (const targetField of candidateTargets) {
        const targetNodeData = targetField.node;
        let hasMatch = false;
        
        // Primary mapping logic
        if (RECORD_TYPE === "VARIABLE" && targetNodeData.ICON_DISPLAY === "VARIABLE") {
          hasMatch = node.MAPNAME === targetNodeData.NAME;
        } else if (RECORD_TYPE === "SEQUENCE" && targetNodeData.ICON_DISPLAY === "SEQUENCE") {
          hasMatch = node.MAPNAME === targetNodeData.NAME;
        } else if (RECORD_TYPE === "PLUGIN" && targetNodeData.ICON_DISPLAY === "PLUGIN") {
          hasMatch = node.PLUGIN_NAME === targetNodeData.NAME;
        } else if (RECORD_TYPE === "CUSTOM_DB_FIELD" && targetNodeData.ICON_DISPLAY === "QUERY") {
          hasMatch = node.CUSTOM_DB_FIELD === targetNodeData.NAME || MAPNAME === targetNodeData.NAME;
        } else if (RECORD_TYPE === "VARIABLE_COLLECTION" && targetNodeData.ICON_DISPLAY === "VARIABLE_COLLECTION") {
          hasMatch = node.MAPNAME === targetNodeData.NAME || MAPNAME === targetNodeData.NAME;
        }

        // Secondary mapping (Mapping2) for conditional if
        if (!hasMatch && node.RECORD_TYPE2) {
          if (node.RECORD_TYPE2 === "VARIABLE" && targetNodeData.ICON_DISPLAY === "VARIABLE") {
            hasMatch = node.MAPNAME2 === targetNodeData.NAME;
          } else if (node.RECORD_TYPE2 === "SEQUENCE" && targetNodeData.ICON_DISPLAY === "SEQUENCE") {
            hasMatch = node.MAPNAME2 === targetNodeData.NAME;
          } else if (node.RECORD_TYPE2 === "PLUGIN" && targetNodeData.ICON_DISPLAY === "PLUGIN") {
            hasMatch = node.PLUGIN_NAME2 === targetNodeData.NAME;
          } else if (node.RECORD_TYPE2 === "CUSTOM_DB_FIELD" && targetNodeData.ICON_DISPLAY === "QUERY") {
            hasMatch = node.CUSTOM_DB_FIELD2 === targetNodeData.NAME || MAPNAME === targetNodeData.NAME;
          } else if (node.RECORD_TYPE2 === "VARIABLE_COLLECTION" && targetNodeData.ICON_DISPLAY === "VARIABLE_COLLECTION") {
            hasMatch = node.MAPNAME2 === targetNodeData.NAME || MAPNAME === targetNodeData.NAME;
          }
        }

        if (hasMatch) {
          addPotentialMapping(sourceField, targetField, sourceTabIndex, targetField.targetTab, 100, 'right-to-right');
        }
      }
    } else {
      // Handle left-to-right mappings
      const exactLookupKey = `${PARENTKEY}|${MAPNAME}|${RECORD_TYPE}`;
      const internalCountKeys = [
        `${PARENTKEY}|INTERNAL_TOTAL_RECTYPE_COUNT|${RECORD_TYPE}`,
        `${PARENTKEY}|INTERNAL_CURRENT_RECTYPE_COUNT|${RECORD_TYPE}`
      ];

      // Check for exact matches
      const exactMatches = targetFieldsByKey.get(exactLookupKey) || [];
      for (const targetField of exactMatches) {
        addPotentialMapping(sourceField, targetField, sourceTabIndex, targetField.targetTab, 100, 'exact');
      }

      // Check for internal count matches
      for (const countKey of internalCountKeys) {
        const countMatches = targetFieldsByKey.get(countKey) || [];
        for (const targetField of countMatches) {
          addPotentialMapping(sourceField, targetField, sourceTabIndex, targetField.targetTab, 90, 'internal_count');
        }
      }

      // Check sub-nodes for complex target types
      const subNodeMatches = targetSubNodesByParent.get(exactLookupKey) || [];
      
      for (const { targetField, subNode } of subNodeMatches) {
        addPotentialMapping(sourceField, targetField, sourceTabIndex, targetField.targetTab, 85, 'sub-node');
      }

      // Check for inbound data matches
      for (const [lookupKey, targetFieldList] of targetFieldsByKey) {
        for (const targetField of targetFieldList) {
          const targetNodeData = targetField.node;
          const targetPARENTKEY = targetNodeData.PARENT || targetNodeData.SOURCE_PARENT || "";
          
          if (PARENTKEY === targetPARENTKEY && 
              node.RECORD_TYPE === targetNodeData.LOOP_SOURCE_NAME && 
              targetNodeData.SOURCE_TYPE === "INBOUND_DATA") {
            addPotentialMapping(sourceField, targetField, sourceTabIndex, targetField.targetTab, 80, 'inbound_data');
          }
        }
      }
    }
  }

  function addPotentialMapping(sourceField, targetField, sourceTabIndex, targetTabIndex, baseScore, matchType) {
    // Validate the mapping before considering it (automatic mapping - more lenient)
    const mappingType = matchType === 'right-to-right' ? 'right-to-right' : 'left-to-right';
    const validation = validateMapping(sourceField.node, targetField.node, true, mappingType, true);
    if (!validation.isValid) return;

    // Calculate final score with same-tab bonus
    let matchScore = baseScore;
    if (sourceTabIndex === targetTabIndex) {
      matchScore += 10; // Same tab bonus
    }
    
    const targetUniqueKey = `${targetField.key}`;
    
    allPotentialMappings.push({
      sourceKey: sourceField.key,
      targetKey: targetField.key,
      targetUniqueKey: targetUniqueKey,
      sourceTab: sourceTabIndex,
      targetTab: targetTabIndex,
      mappingType: matchType === 'right-to-right' ? 'right-to-right' : 'left-to-right',
      isAutomatic: true,
      isCrossTab: sourceTabIndex !== targetTabIndex,
      matchScore: matchScore,
      matchType: matchType,
      sourceNode: sourceField.node,
      targetNode: targetField.node
    });
  }

  // Step 2: Sort by match score (highest first) and select best mapping for each target
  allPotentialMappings.sort((a, b) => b.matchScore - a.matchScore);
  
  const finalMappings = [];
  const usedTargets = new Set();
  
  // Step 3: Process mappings in order of score, ensuring each target gets only one source
  for (const mapping of allPotentialMappings) {
    if (!usedTargets.has(mapping.targetUniqueKey)) {
      // This target hasn't been mapped yet, use this mapping
      finalMappings.push({
        sourceKey: mapping.sourceKey,
        targetKey: mapping.targetKey,
        sourceTab: mapping.sourceTab,
        targetTab: mapping.targetTab,
        mappingType: mapping.mappingType,
        isAutomatic: mapping.isAutomatic,
        isCrossTab: mapping.isCrossTab
      });
      
      usedTargets.add(mapping.targetUniqueKey);
    }
  }

  // Emit end event for cross-tab mapping
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('autoMappingFinished'));
  }
  
  return finalMappings;
};

/**
 * Extract all field and record nodes from tree data with their keys
 * @param {Array} treeData - Tree data to extract fields and records from
 * @param {boolean} isRightToRight - Whether this is for right-to-right mapping
 * @returns {Array} Array of {key, node} objects representing fields and records
 */
const extractFieldsFromTree = (treeData, isRightToRight = false) => {
  const fields = [];
  
  eachDeep(treeData, (node, key, parentValue, context) => {
    if (node && typeof node === 'object' && node.key) {
      // Include both field nodes and record nodes for mapping
      if (isRightToRight) {
        if (isFieldNode(node) || isRecordNode(node) || ["QUERY", "PLUGIN", "SEQUENCE", "VARIABLE", "VARIABLE_COLLECTION", "TRACKING_REPORT_ITEM", "CONDITION_IF"].includes(node.ICON_DISPLAY)) {
          fields.push({
            key: node.key,
            node: node
          });
        }
      }
      else if (isFieldNode(node) || isRecordNode(node)) {
        fields.push({
          key: node.key,
          node: node
        });
      }
    }
  });



  return fields;
};

/**
 * Determine if a node represents a field (vs a record or group)
 * @param {Object} node - Tree node to check
 * @returns {boolean} True if node represents a field
 */
const isFieldNode = (node) => {
  if (!node || typeof node !== 'object') return false;

  // Check various indicators that this is a field node
  const fieldIcons = [
    'FIELD', 'FIELD_ALPHA', 'FIELD_NUMERIC', 'FIELD_DATE', 'FIELD_TIME',
    'FIELD_DATETIME', 'FIELD_BOOLEAN', 'FIELD_BINARY', 'ELEMENT', 'ATTRIBUTE'
  ];

  const recordIcons = [
    'RECORD', 'RECORD_TOP', 'RECORD_LOOP', 'REPEATING_ELEMENT', 'OBJECT', 'QUERY'
  ];

  // Primary check: ICON_DISPLAY indicates field type
  if (node.ICON_DISPLAY && fieldIcons.includes(node.ICON_DISPLAY)) {
    return true;
  }

  // Secondary check: explicitly not a record type
  if (node.ICON_DISPLAY && recordIcons.includes(node.ICON_DISPLAY)) {
    return false;
  }

  // Enhanced tertiary check: has field-like properties
  const hasFieldProperties = node.MAPNAME || node.FIELD_NAME || node.ELEMENT_NAME || node.ATTRIBUTE_NAME;
  const hasNoChildren = !node.children || node.children.length === 0;
  
  // Additional check: if ICON_DISPLAY contains 'FIELD' anywhere, consider it a field
  const iconContainsField = node.ICON_DISPLAY && node.ICON_DISPLAY.includes('FIELD');
  
  // More lenient classification for cross-tab mapping
  if (iconContainsField || (hasFieldProperties && hasNoChildren)) {
    return true;
  }
  
  // Fallback: if no ICON_DISPLAY but has field properties and no children, assume it's a field
  if (!node.ICON_DISPLAY && hasFieldProperties && hasNoChildren) {
    return true;
  }

  return false;
};

/**
 * Determine if a node represents a record
 * @param {Object} node - Tree node to check
 * @returns {boolean} True if node represents a record
 */
export const isRecordNode = (node) => {
  if (!node || typeof node !== 'object') return false;

  const recordIcons = [
    'RECORD', 'RECORD_TOP', 'RECORD_LOOP', 'REPEATING_ELEMENT', 'OBJECT', "QUERY"
  ];

  return node.ICON_DISPLAY && recordIcons.includes(node.ICON_DISPLAY);
};

/**
 * Get the type of a node (field, record, or group)
 * @param {Object} node - Tree node to check
 * @returns {string} Node type: 'field', 'record', or 'group'
 */
export const getNodeType = (node) => {
  if (!node || typeof node !== 'object') return 'unknown';
  
  if (isFieldNode(node)) return 'field';
  if (isRecordNode(node)) return 'record';
  if (["VARIABLE", "SEQUENCE", "PLUGIN", "VARIABLE_COLLECTION", "TRACKING_REPORT_ITEM", "CONDITION_IF", "QUERY"].includes(node.ICON_DISPLAY)) return 'field';
  return 'group';
};

/**
 * Validate right-to-right mapping (field to variable, sequence, plugin, etc.)
 * @param {Object} sourceNode - Source field node
 * @param {Object} targetNode - Target node (variable, sequence, plugin, custom db, etc.)
 * @returns {Object} Validation result
 */
const validateRightToRightMapping = (sourceNode, targetNode) => {
  // Check if source is a field
  const sourceType = getNodeType(sourceNode);
  if (sourceType !== 'field') {
    return {
      isValid: false,
      errorMessage: `Right-to-right mapping requires source to be a field. Source type: ${sourceType}`
    };
  }

  // Check if target is one of the allowed types for right-to-right mapping
  const targetRecordType = targetNode.RECORD_TYPE || '';
  const targetIconDisplay = targetNode.ICON_DISPLAY || '';
  
  const allowedTargetTypes = [
    'VARIABLE',
    'SEQUENCE', 
    'VARIABLE_COLLECTION',
    'PLUGIN',
    'CUSTOM_DB_FIELD',
    'TRACKING_REPORT_ITEM'
  ];

  // Check by RECORD_TYPE first
  if (allowedTargetTypes.includes(targetRecordType)) {
    return validateRightToRightCompatibility(sourceNode, targetNode, targetRecordType);
  }

  // Check by ICON_DISPLAY as fallback
  if (allowedTargetTypes.includes(targetIconDisplay)) {
    return validateRightToRightCompatibility(sourceNode, targetNode, targetIconDisplay);
  }

  return {
    isValid: false,
    errorMessage: `Right-to-right mapping not allowed. Target must be a variable, sequence, variable collection, plugin, or custom DB field. Target type: ${targetRecordType || targetIconDisplay || 'unknown'}`
  };
};

/**
 * Validate compatibility between source field and target type for right-to-right mapping
 * @param {Object} sourceNode - Source field node
 * @param {Object} targetNode - Target node
 * @param {string} targetType - Target type (VARIABLE, SEQUENCE, etc.)
 * @returns {Object} Validation result
 */
const validateRightToRightCompatibility = (sourceNode, targetNode, targetType) => {
  const sourceFieldName = sourceNode.MAPNAME || sourceNode.FIELD_NAME || sourceNode.title || '';
  const targetName = targetNode.MAPNAME || targetNode.title || '';

  switch (targetType) {
    case 'VARIABLE':
      // Field can map to variable if names match or are compatible
      if (sourceNode.ICON_DISPLAY === 'VARIABLE' && sourceNode.TYPE === targetNode.MAPNAME) {
        return { isValid: true, message: `Field "${sourceFieldName}" mapped to variable "${targetName}"` };
      }
      return { isValid: true, message: `Field "${sourceFieldName}" mapped to variable "${targetName}"` };

    case 'SEQUENCE':
      // Field can map to sequence
      return { isValid: true, message: `Field "${sourceFieldName}" mapped to sequence "${targetName}"` };

    case 'VARIABLE_COLLECTION':
      // Field can map to variable collection
      return { isValid: true, message: `Field "${sourceFieldName}" mapped to variable collection "${targetName}"` };

    case 'PLUGIN':
      // Field can map to plugin if compatible
      if (sourceNode.ICON_DISPLAY === 'PLUGIN' && sourceNode.PLUGIN_NAME === targetNode.MAPNAME) {
        return { isValid: true, message: `Field "${sourceFieldName}" mapped to plugin "${targetName}"` };
      }
      return { isValid: true, message: `Field "${sourceFieldName}" mapped to plugin "${targetName}"` };

    case 'CUSTOM_DB_FIELD':
      // Field can map to custom DB field
      return { isValid: true, message: `Field "${sourceFieldName}" mapped to custom DB field "${targetName}"` };

    case 'TRACKING_REPORT_ITEM':
      // Field can map to tracking report item
      return { isValid: true, message: `Field "${sourceFieldName}" mapped to tracking report item "${targetName}"` };

    default:
      return {
        isValid: false,
        errorMessage: `Unsupported right-to-right mapping target type: ${targetType}`
      };
  }
};

/**
 * Validate if a mapping between two nodes is allowed
 * @param {Object} sourceNode - Source node
 * @param {Object} targetNode - Target node
 * @param {boolean} allowRecordMapping - Allow record-to-field and record-to-record mappings
 * @param {string} mappingType - Type of mapping: 'left-to-right' or 'right-to-right'
 * @param {boolean} isAutoMapping - Whether this is automatic mapping (more lenient)
 * @param {number} sourceTab - Source tab index (0: header, 1: body, 2: trailer)
 * @param {number} targetTab - Target tab index (0: header, 1: body, 2: trailer)
 * @returns {Object} Validation result with isValid and errorMessage
 */
export const validateMapping = (sourceNode, targetNode, allowRecordMapping = false, mappingType = 'left-to-right', isAutoMapping = false, sourceTab = null, targetTab = null) => {
  if (!sourceNode || !targetNode) {
    return { isValid: false, errorMessage: 'Source or target node is missing' };
  }

  // Debug logging for tab restriction validation

  // Test case: if sourceTab is 1 and targetTab is 0, this should trigger the restriction

  // Tab restriction validation for link mode (manual mapping)
  if (!isAutoMapping && sourceTab !== null && targetTab !== null) {
    const tabNames = ['header', 'body', 'trailer'];
    const sourceTabName = tabNames[sourceTab];
    const targetTabName = tabNames[targetTab];
    
    
    // Restrict mapping from source body to target header/trailer
    if (sourceTab === 1 && targetTab === 0) {
      const result = {
        isValid: false,
        errorMessage: t("BOX_NO_INBOUND_BODY_IN_OUTBOUND_HEADER")
      };
      return result;
    }

    if (sourceTab === 1 && targetTab === 2) { 
      return {
        isValid: false,
        errorMessage: t("BOX_NO_INBOUND_BODY_IN_OUTBOUND_TRAILER")
      };
    }
    
    if (sourceTab === 2 && targetTab === 1) {
      return {
        isValid: false,
        errorMessage: t("BOX_NO_INBOUND_TRAILER_IN_OUTBOUND_BODY")
      };
    }

  }

  // For right-to-right mappings, use different validation rules
  if (mappingType === 'right-to-right') {
    return validateRightToRightMapping(sourceNode, targetNode);
  }

  const sourceType = getNodeType(sourceNode);
  const targetType = getNodeType(targetNode);

  // Allow different mapping types based on context
  if (allowRecordMapping) {
    // For display purposes, allow record-to-field and record-to-record mappings
    if ((sourceType === 'record' || sourceType === 'field') && (targetType === 'field' || targetType === 'record')) {
      return validateRecordMapping(sourceNode, targetNode);
    }
    // Still allow field-to-field mappings
    if (sourceType === 'field' && targetType === 'field') {
      return validateFieldMapping(sourceNode, targetNode);
    }
    // Other combinations not allowed
    return { 
      isValid: false, 
      errorMessage: `Mapping from ${sourceType} to ${targetType} is not supported.` 
    };
  } else {
    // Enhanced validation for cross-tab mapping - be more lenient
    // If both nodes have field-like properties, allow the mapping even if classification is uncertain
    const sourceHasFieldProps = sourceNode.MAPNAME || sourceNode.FIELD_NAME || sourceNode.ELEMENT_NAME || sourceNode.ATTRIBUTE_NAME;
    const targetHasFieldProps = targetNode.MAPNAME || targetNode.FIELD_NAME || targetNode.ELEMENT_NAME || targetNode.ATTRIBUTE_NAME;
    const sourceNoChildren = !sourceNode.children || sourceNode.children.length === 0;
    const targetNoChildren = !targetNode.children || targetNode.children.length === 0;
    
    // If both seem like fields (have field properties and no children), allow mapping
    if (sourceHasFieldProps && targetHasFieldProps && sourceNoChildren && targetNoChildren) {
      return validateFieldMapping(sourceNode, targetNode);
    }
    
    // Original strict validation - only field-to-field mapping for actual mapping creation
    if ((sourceType !== 'field' || targetType !== 'field') && !isAutoMapping) {
      return { 
        isValid: false, 
        errorMessage: t(`BOX_CAN_ONLY_USE_LINK_MODE_TO_REMAP_IN_FLD_TO_OUT_FLD__TRY_AGAIN`) 
      };
    }
    // Field-specific validation
    return validateFieldMapping(sourceNode, targetNode);
  }
};

/**
 * Validate record-to-field or record-to-record mapping
 * @param {Object} sourceNode - Source record node
 * @param {Object} targetNode - Target node (field or record)
 * @returns {Object} Validation result
 */
const validateRecordMapping = (sourceNode, targetNode) => {
  // For record mappings, we primarily check structural compatibility
  // These are mainly for display and organizational purposes
  
  const targetType = getNodeType(targetNode);
  
  if (targetType === 'field') {
    // Record-to-field mapping - check if the record can logically map to this field
    // This might be used for showing relationships or for aggregate mappings
    return {
      isValid: true,
      message: `Record "${sourceNode.title || sourceNode.MAPNAME}" mapped to field "${targetNode.title || targetNode.MAPNAME}"`
    };
  } else if (targetType === 'record') {
    // Record-to-record mapping - check structural compatibility
    return {
      isValid: true,
      message: `Record "${sourceNode.title || sourceNode.MAPNAME}" mapped to record "${targetNode.title || targetNode.MAPNAME}"`
    };
  }
  
  return { 
    isValid: false, 
    errorMessage: 'Invalid record mapping target' 
  };
};

/**
 * Validate field-to-field mapping
 * @param {Object} sourceNode - Source field node
 * @param {Object} targetNode - Target field node
 * @returns {Object} Validation result
 */
const validateFieldMapping = (sourceNode, targetNode) => {
  // Check for data type compatibility
  const sourceDataType = getFieldDataType(sourceNode);
  const targetDataType = getFieldDataType(targetNode);

  const compatibleTypes = {
    'ALPHA': ['ALPHA', 'NUMERIC', 'DATE', 'TIME', 'DATETIME'], // Alpha can map to most types
    'NUMERIC': ['NUMERIC', 'ALPHA'], // Numeric can map to numeric or alpha
    'DATE': ['DATE', 'ALPHA'], // Date can map to date or alpha
    'TIME': ['TIME', 'ALPHA'], // Time can map to time or alpha
    'DATETIME': ['DATETIME', 'DATE', 'TIME', 'ALPHA'], // DateTime can map to related types
    'BOOLEAN': ['BOOLEAN', 'ALPHA', 'NUMERIC'], // Boolean can map to boolean, alpha, or numeric
    'BINARY': ['BINARY', 'ALPHA'] // Binary can map to binary or alpha
  };

  if (sourceDataType && targetDataType && compatibleTypes[sourceDataType]) {
    if (!compatibleTypes[sourceDataType].includes(targetDataType)) {
      return {
        isValid: false,
        errorMessage: `Cannot map ${sourceDataType} field to ${targetDataType} field. Incompatible data types.`
      };
    }
  }

  // Check field length constraints - but don't show error for length mismatches
  const sourceLength = getFieldLength(sourceNode);
  const targetLength = getFieldLength(targetNode);

  // Skip length validation - allow mappings even if source length exceeds target length
  // if (sourceLength && targetLength && sourceLength > targetLength) {
  //   return {
  //     isValid: false,
  //     errorMessage: `Source field length (${sourceLength}) exceeds target field length (${targetLength}). Data may be truncated.`
  //   };
  // }

  return { isValid: true };
};

/**
 * Get field data type from node
 * @param {Object} node - Field node
 * @returns {string} Data type
 */
const getFieldDataType = (node) => {
  if (!node) return null;

  // Check ICON_DISPLAY first
  if (node.ICON_DISPLAY) {
    if (node.ICON_DISPLAY.includes('ALPHA')) return 'ALPHA';
    if (node.ICON_DISPLAY.includes('NUMERIC')) return 'NUMERIC';
    if (node.ICON_DISPLAY.includes('DATE')) return 'DATE';
    if (node.ICON_DISPLAY.includes('TIME')) return 'TIME';
    if (node.ICON_DISPLAY.includes('DATETIME')) return 'DATETIME';
    if (node.ICON_DISPLAY.includes('BOOLEAN')) return 'BOOLEAN';
    if (node.ICON_DISPLAY.includes('BINARY')) return 'BINARY';
  }

  // Check DATA_TYPE property
  if (node.DATA_TYPE) {
    return node.DATA_TYPE.toUpperCase();
  }

  // Check FIELD_TYPE property
  if (node.FIELD_TYPE) {
    return node.FIELD_TYPE.toUpperCase();
  }

  // Default to ALPHA if no specific type found
  return 'ALPHA';
};

/**
 * Get field length from node
 * @param {Object} node - Field node
 * @returns {number|null} Field length
 */
const getFieldLength = (node) => {
  if (!node) return null;

  // Check various length properties
  const lengthProps = ['LENGTH', 'FIELD_LENGTH', 'MAX_LENGTH', 'SIZE'];
  
  for (const prop of lengthProps) {
    if (node[prop] && !isNaN(parseInt(node[prop]))) {
      return parseInt(node[prop]);
    }
  }

  return null;
};

/**
 * Extract possible field names from a node
 * @param {Object} node - Tree node
 * @returns {Array} Array of possible field names
 */
const getFieldNames = (node) => {
  if (!node || typeof node !== 'object') return [];

  const names = [];
  
  // Common field name properties
  const nameProps = [
    'MAPNAME', 'FIELD_NAME', 'ELEMENT_NAME', 'ATTRIBUTE_NAME', 
    'DESCRIPTION', 'NAME', 'title'
  ];

  nameProps.forEach(prop => {
    if (node[prop] && typeof node[prop] === 'string' && node[prop].trim()) {
      names.push(node[prop].trim());
    }
  });

  // Remove duplicates and empty values
  return [...new Set(names)].filter(name => name && name.length > 0);
};

/**
 * Get mappings that should be displayed for a selected node
 * @param {Array} allMappings - All manual mappings
 * @param {Array} automaticMappings - Automatic mappings
 * @param {Object} selectedSourceNode - Currently selected source node
 * @param {Object} selectedTargetNode - Currently selected target node
 * @param {boolean} showOnlySelected - Whether to filter by selected node
 * @returns {Array} Filtered mappings to display
 */
export const getVisibleMappings = (allMappings, automaticMappings, selectedSourceNode, selectedTargetNode, showOnlySelected) => {
  // Create a map to track which target keys are used by manual mappings
  const manualTargetKeys = new Set();
  allMappings.forEach(mapping => {
    manualTargetKeys.add(`${mapping.targetKey}-${mapping.sourceTab || 'null'}`);
  });

  // Filter automatic mappings to exclude those that conflict with manual mappings
  // But be more lenient - only exclude exact conflicts
  const filteredAutomaticMappings = automaticMappings.filter(autoMapping => {
    const targetKeyWithTab = `${autoMapping.targetKey}-${autoMapping.sourceTab || 'null'}`;
    return !manualTargetKeys.has(targetKeyWithTab);
  });

  // Combine manual mappings with filtered automatic mappings
  const combinedMappings = [...allMappings, ...filteredAutomaticMappings];



  // If showOnlySelected is true and no nodes are selected, show no mappings
  if (showOnlySelected && !selectedSourceNode && !selectedTargetNode) {
    return [];
  }

  // If showOnlySelected is false, show all mappings
  if (!showOnlySelected) {
    return combinedMappings;
  }

  // Filter mappings to show only those connected to the selected nodes
  // This applies equally to both manual and automatic mappings
  return combinedMappings.filter(mapping => {
    const sourceMatch = selectedSourceNode && (
      mapping.sourceKey === selectedSourceNode.key ||
      mapping.sourceKey === String(selectedSourceNode.key)
    );
    const targetMatch = selectedTargetNode && (
      mapping.targetKey === selectedTargetNode.key ||
      mapping.targetKey === String(selectedTargetNode.key)
    );
    
    return sourceMatch || targetMatch;
  });
};

/**
 * Check if a node matches another node based on field names
 * @param {Object} sourceNode - Source node
 * @param {Object} targetNode - Target node
 * @returns {boolean} True if nodes have matching field names
 */
export const nodesMatch = (sourceNode, targetNode) => {
  if (!sourceNode || !targetNode) return false;

  const sourceNames = getFieldNames(sourceNode);
  const targetNames = getFieldNames(targetNode);

  return sourceNames.some(sourceName => 
    targetNames.some(targetName => 
      sourceName && targetName && 
      sourceName.toLowerCase() === targetName.toLowerCase()
    )
  );
};

/**
 * Find all parent keys that need to be expanded to reveal a target node
 * @param {Array} treeData - Tree data to search
 * @param {string} targetKey - Key of the node to reveal
 * @returns {Array} Array of parent keys that need to be expanded
 */
export const findParentKeysToExpand = (treeData, targetKey) => {
  const keysToExpand = [];
  
  const findNodePath = (nodes, targetKey, currentPath = []) => {
    for (const node of nodes) {
      const newPath = [...currentPath, node.key];
      
      if (node.key === targetKey) {
        // Found the target, return all parent keys (exclude the target itself)
        return currentPath;
      }
      
      if (node.children && node.children.length > 0) {
        const result = findNodePath(node.children, targetKey, newPath);
        if (result) {
          return result;
        }
      }
    }
    return null;
  };
  
  const path = findNodePath(treeData, targetKey);
  return path || [];
};

/**
 * Get keys that need to be expanded for mapped nodes
 * @param {Array} mappings - Array of mappings
 * @param {string} selectedNodeKey - Key of selected node
 * @param {Array} targetTreeData - Tree data of the target tree
 * @param {string} direction - 'source-to-target' or 'target-to-source'
 * @returns {Array} Array of keys that need to be expanded
 */
export const getKeysToExpandForMappedNodes = (mappings, selectedNodeKey, targetTreeData, direction = 'source-to-target') => {
  if (!mappings || !selectedNodeKey || !targetTreeData) return [];
  
  const keysToExpand = new Set();
  
  mappings.forEach(mapping => {
    let targetNodeKey = null;
    
    if (direction === 'source-to-target' && 
        (mapping.sourceKey === selectedNodeKey || mapping.sourceKey === String(selectedNodeKey))) {
      targetNodeKey = mapping.targetKey;
    } else if (direction === 'target-to-source' && 
               (mapping.targetKey === selectedNodeKey || mapping.targetKey === String(selectedNodeKey))) {
      targetNodeKey = mapping.sourceKey;
    }
    
    if (targetNodeKey) {
      const parentKeys = findParentKeysToExpand(targetTreeData, targetNodeKey);
      parentKeys.forEach(key => keysToExpand.add(key));
    }
  });
  
  return Array.from(keysToExpand);
};

/**
 * Find a node in tree data by its key
 * @param {Array} treeData - Tree data to search
 * @param {string} targetKey - Key to find
 * @returns {Object|null} Found node or null
 */
export const findNodeByKey = (treeData, targetKey) => {
  if (!treeData || !Array.isArray(treeData) || !targetKey) return null;
  
  let foundNode = null;
  
  eachDeep(treeData, (node, key, parentValue, context) => {
    if (node && typeof node === 'object' && node.key === targetKey) {
      foundNode = node;
      return false; // Stop iteration
    }
  });
  
  return foundNode;
};

/**
 * Find all AI-mapped fields in a tree that have AI_CONFIDENCE property
 * @param {Array} treeData - Tree data to search
 * @returns {Array} Array of nodes with AI_CONFIDENCE property
 */
export const findAIMappedFields = (treeData) => {
  if (!treeData || !Array.isArray(treeData)) {
    return [];
  }

  const aiMappedFields = [];
  
  eachDeep(treeData, (node) => {
    if (node && node.hasOwnProperty('AI_CONFIDENCE') && node.AI_CONFIDENCE) {
      aiMappedFields.push(node);
    }
    if (Array.isArray(node?.children)) {
      return node.children;
    }
  });

  return aiMappedFields;
};

/**
 * Find all fields that were marked for AI auto-mapping (either with AI_CONFIDENCE or AI_AUTOMAP)
 * @param {Array} treeData - Tree data to search
 * @returns {Array} Array of nodes with AI mapping properties
 */
export const findAITargetFields = (treeData) => {
  if (!treeData || !Array.isArray(treeData)) {
    return [];
  }

  const aiTargetFields = [];
  
  eachDeep(treeData, (node) => {
    if (node && ((node.hasOwnProperty('AI_CONFIDENCE') && node.AI_CONFIDENCE) || 
                (node.hasOwnProperty('AI_AUTOMAP') && node.AI_AUTOMAP === "TRUE"))) {
      aiTargetFields.push(node);
    }
    if (Array.isArray(node?.children)) {
      return node.children;
    }
  });

  return aiTargetFields;
};

/**
 * Get keys of all AI-mapped fields across all tabs
 * @param {Object} rightTreeData - Right tree data with tabs (header, body, trailer)
 * @returns {Object} Object with tab names as keys and arrays of AI-mapped field keys as values
 */
export const getAIMappedFieldKeys = (rightTreeData) => {
  const aiMappedKeys = {
    header: [],
    body: [],
    trailer: []
  };

  if (!rightTreeData) {
    return aiMappedKeys;
  }

  // Check each tab for AI-mapped fields
  ['header', 'body', 'trailer'].forEach(tab => {
    const tabData = rightTreeData[tab]?.translatedData;
    if (tabData) {
      const aiFields = findAIMappedFields(tabData);
      aiMappedKeys[tab] = aiFields.map(field => field.key).filter(key => key);
    }
  });

  return aiMappedKeys;
};

export default {
  findAutomaticMappings,
  findCrossTabAutomaticMappings,
  getVisibleMappings,
  nodesMatch,
  findParentKeysToExpand,
  getKeysToExpandForMappedNodes,
  validateMapping,
  getNodeType,
  findNodeByKey,
  findAIMappedFields,
  getAIMappedFieldKeys
};
